import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { validateEmail } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { BlurView } from 'expo-blur';
import { router } from 'expo-router';
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('LOGIN');

export function LoginScreen() {
  const { login, sendVerificationOTP } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    if (!password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await login(email.trim(), password);

      if (result.error) {
        const isVerificationError = result.error.status === 403;

        if (isVerificationError) {
          logger.log('Email verification required, redirecting to OTP page');
          try {
            await sendVerificationOTP(email.trim());
          } catch {
            logger.error('OTP sending error');
          }
          router.replace({ pathname: '/(auth)/verify-otp', params: { email: email.trim() } });
        } else {
          logger.error('Login failed:', result.error);
          toast.error(result.error.message || 'Login failed. Please check your credentials and try again.');
        }
      }
    } catch {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthScreenLayout title="Log In">
      <BlurView intensity={50} className="rounded-xl overflow-hidden p-6" style={{ width: '100%' }}>
        <View className="space-y-4 p-6">
          <TextInput
            placeholder="Email"
            value={email}
            onChangeText={(text) => {
              setEmail(text);
              if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
            }}
            autoCapitalize="none"
            keyboardType="email-address"
            error={errors.email || undefined}
            containerClassName="my-1"
          />

          <TextInput
            placeholder="Password"
            value={password}
            onChangeText={(text) => {
              setPassword(text);
              if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
            }}
            secureTextEntry
            error={errors.password || undefined}
            containerClassName="my-1"
          />

          <View className="flex-row justify-end mt-2">
            <Pressable onPress={() => router.push({ pathname: '/(auth)/forgot-password' })}>
              <Text className="text-sm underline text-warm-gray-100">Forgot password?</Text>
            </Pressable>
          </View>

          <Pressable
            className="p-4 my-4 rounded-lg bg-primary"
            onPress={handleLogin}
            disabled={isLoading}
          >
            <Text className="font-semibold text-center text-warm-gray-950">
              {isLoading ? 'Logging in...' : 'Log In'}
            </Text>
          </Pressable>
        </View>
      </BlurView>

      <View className="flex-row justify-center mt-6">
        <Text className="text-warm-gray-100">Don&apos;t have an account?{' '}</Text>
        <Pressable onPress={() => router.push({ pathname: '/(auth)/register' })}>
          <Text className="font-semibold underline text-warm-gray-100">Sign Up</Text>
        </Pressable>
      </View>
    </AuthScreenLayout>
  );
}
